import React, {type FC, useEffect, useMemo, useRef} from 'react';
import {BackgroundVideo, StreamGrid} from '../types/Streaming.styled.ts';
import useGlobalStore from '../../../store/useGlobalStore.ts';
import useStreamingStore from '../../../store/useStreamingStore.ts';
import useJitsiConnection from '../../../hooks/useJitsiConnection.tsx';
import useMonitorConnection from '../../../hooks/useMonitorConnection.tsx';
import LocalCell from './LocalCell.tsx';
import StudioCell from './StudioCell.tsx';
import {getCellLayout, mergeRemoteTracksByDisplayName} from '../../../utils/StreamingUtil.ts';
import RemoteCell from './RemoteCell.tsx';
import {useLocation} from 'react-router';
import useCenterStore from '../../../store/useCenterStore.ts';
import type {JitsiTrackState} from '../types/lib-jitsi-meet';

/** center_id 기반 0~maxMs 범위의 안정적인 지연값 생성 (같은 센터 = 같은 딜레이) */
const getStableJitter = (seed: string, maxMs: number): number => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % maxMs;
};

const Streaming: FC = () => {
    const location = useLocation();
    const isWatchingPage = location.pathname.includes('Watching');
    const {isFullScreen} = useGlobalStore();
    const {state, gridLayout} = useStreamingStore();
    const {centerInfo} = useCenterStore();
    const {getConnection, leave, studioTrack, localTrack, localTrackVersion, remoteTrackVersion, remoteTracks, excludedParticipants, displayedParticipants, selectedByStudio, selectedByStudioOrder, excludedByStudio, studioStateVersion, triggerConstraintUpdate} = useJitsiConnection();

    const gridInfo = getCellLayout(gridLayout.mode);

    const isLargeGrid = gridInfo.remoteSize > 50;
    const monitor = useMonitorConnection();

    // 대규모 레이아웃 진입/이탈 시 봇 연결 생성/해제
    useEffect(() => {
        if (isLargeGrid && state.jwt) {
            monitor.connect();
            return () => {
                monitor.leave();
            };
        }
    }, [isLargeGrid, state.jwt]);

    // 두 풀 merge: 대규모면 origin(own)+봇(bot)을 displayName 키로 dedupe, 아니면 origin 그대로
    const mergedRemoteTracks = useRef<Map<string, JitsiTrackState>>(new Map());
    const mergedVersion = useMemo(() => {
        if (isLargeGrid) {
            mergedRemoteTracks.current = mergeRemoteTracksByDisplayName(
                remoteTracks.current,
                monitor.remoteTracks.current
            );
        } else {
            mergedRemoteTracks.current = remoteTracks.current;
        }
        return remoteTrackVersion + monitor.remoteTrackVersion;
    }, [isLargeGrid, remoteTrackVersion, monitor.remoteTrackVersion]);

    const jitterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!state.jwt) return;
        let cancelled = false;

        const initializeJitsi = async () => {
            try {
                if (window.JitsiMeetJS) {
                    // FHD 카메라 연결 확인 및 MediaStream 사전 생성
                    const {videoInputD, audioInputD, cameraEnable, mediaStream: existingStream} = useCenterStore.getState();
                    if (!existingStream && cameraEnable && videoInputD?.deviceId) {
                        try {
                            const stream = await navigator.mediaDevices.getUserMedia({
                                video: {deviceId: {exact: videoInputD.deviceId}},
                                audio: audioInputD?.deviceId
                                    ? {deviceId: {exact: audioInputD.deviceId}}
                                    : true,
                            });
                            if (cancelled) {
                                stream.getTracks().forEach((t) => t.stop());
                                return;
                            }
                            useCenterStore.getState().setMediaStream(stream);
                        } catch (e) {
                            console.warn('FHD 카메라 연결 실패, 기본 카메라로 폴백:', e);
                        }
                    }
                    if (cancelled) return;

                    window.JitsiMeetJS.init({
                        disableAudioLevels: true,
                        disableSimulcast: false,
                        enableWindowOnErrorHandler: false,
                        disableThirdPartyRequests: true,
                        useIPv6: false,
                        enableAnalyticsLogging: false,
                    });
                    // ~200대 동시 접속 thundering herd 방지: 센터별 0~15초 랜덤 지연
                    const jitterMs = getStableJitter(centerInfo?.center_id || '', 15000);
                    await new Promise<void>((resolve) => {
                        jitterTimerRef.current = setTimeout(resolve, jitterMs);
                    });
                    if (cancelled) return;
                    await getConnection();
                }
            } catch (error) {
                console.error('Jitsi load Fail', error);
            }
        };

        initializeJitsi();

        return () => {
            cancelled = true;
            if (jitterTimerRef.current) {
                clearTimeout(jitterTimerRef.current);
                jitterTimerRef.current = null;
            }
        };
    }, [state.jwt]);

    useEffect(() => {
        return () => {
            leave();
        };
    }, [leave]);

    const studioElemRef = useRef<HTMLVideoElement>(null);
    const localElemRef = useRef<HTMLVideoElement>(null);
    const remotesElemRef = useRef<Array<HTMLVideoElement>>([]);

    return (
        <BackgroundVideo
            $isFullScreen={isFullScreen}
            $isWatching={isWatchingPage}
        >
            <StreamGrid
                $col={gridInfo.gridCol}
                $row={gridInfo.gridRow}
                $gap={'10px'}
            >
                {/* Studio Cell */}
                <StudioCell
                    studioElemRef={studioElemRef}
                    studioTrack={studioTrack}
                    gridInfo={gridInfo}
                    isWatching={isWatchingPage}
                />
                {/* Local Cell */}
                <LocalCell
                    localElemRef={localElemRef}
                    localTrack={localTrack}
                    localTrackVersion={localTrackVersion}
                    gridInfo={gridInfo}
                    isIsland={!isFullScreen && gridInfo.studioColSpan === 12}
                />
                {/* Remote Cell */}
                <RemoteCell
                    remoteElemRef={remotesElemRef}
                    remoteTracks={mergedRemoteTracks}
                    remoteTrackVersion={mergedVersion}
                    remoteSize={gridInfo.remoteSize}
                    gridInfo={gridInfo}
                    excludedParticipants={excludedParticipants}
                    displayedParticipants={displayedParticipants}
                    selectedByStudio={selectedByStudio}
                    selectedByStudioOrder={selectedByStudioOrder}
                    excludedByStudio={excludedByStudio}
                    studioStateVersion={studioStateVersion}
                    isWatching={isWatchingPage}
                    onConstraintUpdate={triggerConstraintUpdate}
                />
            </StreamGrid>
        </BackgroundVideo>
    );
};

export default Streaming;
